import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, RefreshCw, Search, Eye, Copy, Check, Lock, User } from 'lucide-react';
import api from '../api/api';

// How often the list re-fetches on its own while the panel is open, so support
// can sit on this page while a user requests a code.
const AUTO_REFRESH_MS = 15000;

function formatAgo(seconds) {
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
}

function formatCountdown(seconds) {
    if (seconds <= 0) return 'expired';
    const m = Math.floor(seconds / 60);
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
}

/**
 * Admin support tool on System Logs: lists pending signup / password-reset
 * verification codes and reveals one on request. The list never includes the
 * codes themselves — each reveal is a separate call the backend logs to System
 * Logs with the viewing admin.
 */
export default function VerificationCodesPanel({ isDarkMode }) {
    const [codes, setCodes] = useState([]);
    const [fetchedAt, setFetchedAt] = useState(Date.now());
    const [now, setNow] = useState(Date.now());
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [revealed, setRevealed] = useState({}); // emailOTPId -> code
    const [revealing, setRevealing] = useState(null);
    const [revealError, setRevealError] = useState({}); // emailOTPId -> message
    const [copiedId, setCopiedId] = useState(null);

    const fetchCodes = useCallback(async (showRefresh = false) => {
        if (showRefresh) setRefreshing(true);
        setError(null);
        try {
            const params = search ? { search } : {};
            const res = await api.get('/admin/otp-codes', { params });
            const list = res.data.codes || [];
            setCodes(list);
            setFetchedAt(Date.now());
            // Drop revealed codes that are no longer pending (used, replaced, expired).
            const ids = new Set(list.map((c) => c.emailOTPId));
            setRevealed((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => ids.has(Number(id)))));
        } catch (err) {
            setError('Failed to load verification codes');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [search]);

    useEffect(() => {
        fetchCodes();
        const interval = setInterval(() => fetchCodes(), AUTO_REFRESH_MS);
        return () => clearInterval(interval);
    }, [fetchCodes]);

    // Tick once a second so the expiry countdowns stay live between fetches.
    useEffect(() => {
        const tick = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(tick);
    }, []);

    const elapsed = Math.floor((now - fetchedAt) / 1000);

    const handleSearch = (e) => {
        e.preventDefault();
        const next = searchInput.trim();
        if (next === search) {
            fetchCodes(true);
            return;
        }
        setLoading(true);
        setSearch(next);
    };

    const handleReveal = async (id) => {
        setRevealing(id);
        setRevealError((prev) => ({ ...prev, [id]: null }));
        try {
            const res = await api.post(`/admin/otp-codes/${id}/reveal`);
            setRevealed((prev) => ({ ...prev, [id]: res.data.code }));
        } catch (err) {
            const message = err.response?.data?.error || 'Could not reveal this code';
            setRevealError((prev) => ({ ...prev, [id]: message }));
            if (err.response?.status === 404) fetchCodes();
        } finally {
            setRevealing(null);
        }
    };

    const handleCopy = (id, code) => {
        navigator.clipboard.writeText(code);
        setCopiedId(id);
        setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 2000);
    };

    const card = isDarkMode ? 'bg-slate-800/60 border-slate-700/60' : 'bg-white border-slate-200';
    const muted = isDarkMode ? 'text-slate-400' : 'text-slate-500';
    const strong = isDarkMode ? 'text-slate-100' : 'text-slate-900';

    return (
        <section className={`mb-8 rounded-xl border p-5 ${card}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-50 text-indigo-600'}`}>
                        <KeyRound size={20} />
                    </div>
                    <div>
                        <h2 className={`text-lg font-bold ${strong}`}>Verification codes</h2>
                        <p className={`text-sm ${muted}`}>
                            Pending signup and password reset codes. Updates every 15 seconds. Every reveal is recorded in System Logs.
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => fetchCodes(true)}
                    disabled={refreshing}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-white hover:bg-slate-50 text-slate-600 border border-slate-200'}`}
                >
                    <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                    Refresh
                </button>
            </div>

            <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                    <Search size={18} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Filter by email, then press Enter..."
                        className={`w-full pl-10 pr-4 py-2.5 rounded-lg text-sm border transition-colors ${
                            isDarkMode
                                ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500 focus:border-indigo-500'
                                : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-400'
                        } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
                    />
                </div>
            </form>

            {loading ? (
                <p className={`text-sm py-6 text-center ${muted}`}>Loading codes…</p>
            ) : error ? (
                <p className="text-sm py-6 text-center text-red-500">{error}</p>
            ) : codes.length === 0 ? (
                <div className="text-center py-8">
                    <KeyRound size={36} className={`mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
                    <p className={`text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>No pending codes</p>
                    <p className={`text-sm mt-1 ${muted}`}>
                        {search ? `Nothing pending for "${search}". ` : ''}Ask the user to request a new code — it will show up here within 15 seconds.
                    </p>
                </div>
            ) : (
                <div className="space-y-2">
                    {codes.map((c) => {
                        const remaining = c.secondsUntilExpiry - elapsed;
                        const code = revealed[c.emailOTPId];
                        return (
                            <div
                                key={c.emailOTPId}
                                className={`flex flex-wrap items-center gap-4 p-4 rounded-lg border ${isDarkMode ? 'border-slate-700/60 bg-slate-900/30' : 'border-slate-200 bg-slate-50/60'}`}
                            >
                                <div className="flex-1 min-w-[12rem]">
                                    <p className={`font-semibold text-sm break-all ${strong}`}>{c.email}</p>
                                    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs mt-1 ${muted}`}>
                                        {c.clientName && (
                                            <span className="flex items-center gap-1"><User size={12} />{c.clientName}</span>
                                        )}
                                        <span>Sent {formatAgo(c.secondsSinceSent + elapsed)}</span>
                                        <span className={remaining < 120 ? 'text-amber-500 font-medium' : ''}>
                                            Expires in {formatCountdown(remaining)}
                                        </span>
                                        {c.failedAttempts > 0 && <span>{c.failedAttempts} failed attempt{c.failedAttempts === 1 ? '' : 's'}</span>}
                                        {c.isLocked === 1 && (
                                            <span className="flex items-center gap-1 text-red-500 font-medium"><Lock size={12} />Locked out</span>
                                        )}
                                    </div>
                                    {revealError[c.emailOTPId] && (
                                        <p className="text-xs mt-1 text-red-500">{revealError[c.emailOTPId]}</p>
                                    )}
                                </div>

                                {code ? (
                                    <div className="flex items-center gap-2">
                                        <span className={`font-mono text-2xl font-bold tracking-[0.3em] ${strong}`}>{code}</span>
                                        <button
                                            onClick={() => handleCopy(c.emailOTPId, code)}
                                            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-200 text-slate-600'}`}
                                            title="Copy code"
                                        >
                                            {copiedId === c.emailOTPId ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => handleReveal(c.emailOTPId)}
                                        disabled={revealing === c.emailOTPId || remaining <= 0}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <Eye size={16} />
                                        {revealing === c.emailOTPId ? 'Revealing…' : 'Reveal code'}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}
